<script setup lang="tsx">
import { computed, onMounted, ref } from 'vue';
import type { DataTableColumns } from 'naive-ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { fetchGetNotifications } from '@/service/api';
import { useRouterPush } from '@/hooks/common/router';
import { useAuthStore } from '@/store/modules/auth';

defineOptions({ name: 'SubscriptionNotificationModal' });

dayjs.extend(utc);

type DetailPage = 'summary' | 'metrics' | 'applications';

const authStore = useAuthStore();
const { routerPushByKey } = useRouterPush(false);
const guideVisible = ref(false);
const visible = ref(false);
const loading = ref(false);
const page = ref<DetailPage>('summary');
const notifications = ref<Api.Auth.Notifications>({ metricsAlerts: [], applicationAlerts: [] });

const metricsCount = computed(() => notifications.value.metricsAlerts.length);
const applicationCount = computed(() => notifications.value.applicationAlerts.length);

function sortByAppName<T extends { appName: string }>(alerts: T[]) {
  return [...alerts].sort((left, right) => left.appName.localeCompare(right.appName));
}

const sortedMetricsAlerts = computed(() => sortByAppName(notifications.value.metricsAlerts));
const sortedApplicationAlerts = computed(() => sortByAppName(notifications.value.applicationAlerts));

const bandOrder = ['pipeline', 'acute', 'chronic'];
const bandFallback: Record<string, string> = {
  pipeline: '数据管道',
  acute: '急性异常（昨日 / 同星期）',
  chronic: '慢性下滑（30 天 / 同期群）',
  unknown: '其他告警'
};

const metricAlertGroups = computed(() => {
  const groups = new Map<string, { key: string; band: string; label: string; alerts: Api.Auth.MetricsAlert[] }>();
  notifications.value.metricsAlerts.forEach(alert => {
    const band = alert.band || 'unknown';
    const label = alert.bandName || bandFallback[band] || band;
    const key = `${band}\0${label}`;
    const group = groups.get(key);
    if (group) {
      group.alerts.push(alert);
    } else {
      groups.set(key, { key, band, label, alerts: [alert] });
    }
  });

  return [...groups.values()].sort((left, right) => {
    const leftIndex = bandOrder.indexOf(left.band);
    const rightIndex = bandOrder.indexOf(right.band);
    const normalizedLeft = leftIndex === -1 ? bandOrder.length : leftIndex;
    const normalizedRight = rightIndex === -1 ? bandOrder.length : rightIndex;
    return normalizedLeft - normalizedRight || left.label.localeCompare(right.label);
  });
});

const metricsSummary = computed(() => {
  const detail = metricAlertGroups.value.map(group => `${group.label} ${group.alerts.length} 条`).join('；');
  return `你订阅的应用中，共 ${metricsCount.value} 条告警${detail ? `：${detail}` : ''}。`;
});

const applicationSummary = computed(
  () => `应用状态异常共 ${applicationCount.value} 个（除了已在 BI 系统设置忽略的外）。`
);

const levelText: Record<number, string> = { 1: '🟡 提示', 2: '🟠 警告', 3: '🔴 严重' };

function formatUtcTime(value: string | null) {
  if (!value) return '-';

  const time = dayjs.utc(value);
  return time.isValid() ? time.format('YYYY-MM-DD HH:mm:ss') : value;
}

const metricsColumns: DataTableColumns<Api.Auth.MetricsAlert> = [
  { title: '级别', key: 'level', width: 64, render: row => levelText[row.level] ?? String(row.level) },
  {
    title: '超出倍数',
    key: 'breachMultiple',
    width: 72,
    render: row =>
      row.breachMultiple === null || row.breachMultiple === undefined
        ? '-'
        : `${Number(row.breachMultiple.toFixed(1))}×`
  },
  { title: '应用', key: 'appName', width: 105, ellipsis: { tooltip: true } },
  { title: '维度', key: 'scope', width: 62, ellipsis: { tooltip: true } },
  { title: '指标', key: 'metricName', width: 135 },
  {
    title: '告警类型',
    key: 'bandName',
    width: 150,
    render: row => row.bandName || bandFallback[row.band || 'unknown'] || '其他告警'
  },
  {
    title: '类型',
    key: 'kinds',
    width: 95,
    render: row => row.kinds?.join('、') || '-'
  },
  {
    title: '说明',
    key: 'details',
    minWidth: 280,
    render: row => row.details?.join('；') || '-'
  },
  { title: '告警时间', key: 'alertTime', width: 165, render: row => formatUtcTime(row.alertTime) }
];

const applicationColumns: DataTableColumns<Api.Auth.ApplicationAlert> = [
  { title: '应用名', key: 'appName', width: 130, ellipsis: { tooltip: true } },
  { title: '包名', key: 'packageName', width: 190, ellipsis: { tooltip: true } },
  { title: '商店名称', key: 'storeName', width: 130, ellipsis: { tooltip: true }, render: row => row.storeName || '-' },
  {
    title: '运营账号',
    key: 'operationAccount',
    width: 170,
    ellipsis: { tooltip: true },
    render: row => row.operationAccount || '-'
  },
  { title: '告警状态', key: 'alarmStatus', width: 100 },
  { title: '下架日期', key: 'removalTime', width: 100, render: row => row.removalTime || '-' }
];

const title = computed(() => {
  if (page.value === 'metrics') return '指标告警详情';
  if (page.value === 'applications') return '应用状态异常详情';
  return '消息通知';
});

async function loadNotifications() {
  const userInfoReady = await authStore.refreshUserInfo();
  if (!userInfoReady) return;
  if (authStore.userInfo.subscriptionInfo?.subscriptionGuided !== true) {
    guideVisible.value = true;
    return;
  }
  if (authStore.userInfo.subscriptionInfo?.enabled !== true) return;

  loading.value = true;
  try {
    const { data, error } = await fetchGetNotifications();
    if (error || !data) return;
    notifications.value = {
      metricsAlerts: data.metricsAlerts ?? [],
      applicationAlerts: data.applicationAlerts ?? []
    };
    visible.value = metricsCount.value > 0 || applicationCount.value > 0;
  } finally {
    loading.value = false;
  }
}

function ignore() {
  visible.value = false;
  page.value = 'summary';
}

function goToSubscriptionSettings() {
  guideVisible.value = false;
  routerPushByKey('user-center');
}

onMounted(loadNotifications);
</script>

<template>
  <NModal
    v-model:show="guideVisible"
    preset="card"
    title="信息订阅功能指引"
    style="width: min(88vw, 520px)"
    :closable="false"
    :mask-closable="false"
    :close-on-esc="false"
  >
    <p class="subscription-guide__content">现已新增异常信息订阅功能，请前往个人中心配置订阅信息。</p>
    <template #footer>
      <div class="subscription-guide__footer">
        <NButton @click="guideVisible = false">忽略</NButton>
        <NButton type="primary" @click="goToSubscriptionSettings">前往个人中心</NButton>
      </div>
    </template>
  </NModal>

  <NModal
    v-model:show="visible"
    preset="card"
    :title="title"
    :style="{ width: page === 'summary' ? 'min(88vw, 760px)' : 'min(96vw, 1240px)' }"
    :closable="false"
    :mask-closable="false"
    :close-on-esc="false"
    class="notification-modal"
  >
    <template v-if="page === 'summary'">
      <div class="notification-summary">
        <div class="notification-summary__row">
          <p>{{ metricsSummary }}</p>
          <NButton text type="primary" @click="page = 'metrics'">查看详情</NButton>
        </div>
        <div class="notification-summary__row">
          <p>{{ applicationSummary }}</p>
          <NButton text type="primary" @click="page = 'applications'">查看详情</NButton>
        </div>
      </div>
    </template>

    <template v-else-if="page === 'metrics'">
      <p class="notification-detail__intro">{{ metricsSummary }}</p>
      <NDataTable
        :columns="metricsColumns"
        :data="sortedMetricsAlerts"
        :pagination="{ pageSize: 6 }"
        size="small"
        striped
      />
    </template>

    <template v-else>
      <p class="notification-detail__intro">{{ applicationSummary }}</p>
      <NDataTable
        :columns="applicationColumns"
        :data="sortedApplicationAlerts"
        :pagination="{ pageSize: 6 }"
        size="small"
        striped
      />
    </template>

    <template #footer>
      <div class="notification-modal__footer">
        <NButton v-if="page !== 'summary'" @click="page = 'summary'">返回</NButton>
        <NButton v-else type="primary" @click="ignore">忽略</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.notification-summary {
  display: grid;
  gap: 12px;
}

.subscription-guide__content {
  margin: 8px 0;
  line-height: 1.8;
}

.subscription-guide__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.notification-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px;
  border: 1px solid rgb(var(--base-text-color) / 0.09);
  border-radius: 8px;
}

.notification-summary__row p,
.notification-detail__intro {
  margin: 0;
  line-height: 1.8;
}

.notification-detail__intro {
  margin-bottom: 16px;
}

.notification-modal__footer {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 640px) {
  .notification-summary__row {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
